import React from 'react';

import { useIntl } from 'ha/i18n';

import * as SearchFilters from 'ha/types/SearchFilters';
import { formatDateRange } from '@ha/date';

import { priceRange } from 'ha/utils/formatRanges';
import { Chip, Grid } from '@ha/dsl';

import { getTagList } from './utils/getTagList';

interface Props {
  types?: SearchFilters.TypeFilterValue;
  furniture?: SearchFilters.FurnitureFilterValue;
  bills?: SearchFilters.BillsFilterValue;
  registration?: SearchFilters.RegistrationFilterValue;
  suitableFor?: SearchFilters.SuitableForFilterValue;
  recentlyAdded?: SearchFilters.RecentlyAddedFilterValue;
  rules?: SearchFilters.RulesFilterValue;
  gender?: SearchFilters.GenderFilterValue;
  rooms?: SearchFilters.RoomsFilterValue;
  rate?: number;
  price?: SearchFilters.PriceFilterValue | null;
  dates?: SearchFilters.DateFilterValue;
  searchArea?: SearchFilters.SearchAreaFilterValue;
  propertySize?: SearchFilters.PropertySizeFilterValue;
  facilities?: SearchFilters.FacilitiesFilterValue;
  amenities?: SearchFilters.AmenitiesFilterValue;
  radius?: SearchFilters.RadiusFilterValue;
  onRemove: (filter: string, value?: string) => void;
}

const FiltersTags: React.FC<Props> = ({
  types = [],
  furniture = [],
  bills = [],
  suitableFor = [],
  registration = [],
  rules = [],
  recentlyAdded,
  gender = [],
  facilities = [],
  amenities = [],
  radius,
  rooms = {
    bedroomCount: [],
  },
  searchArea = [],
  price,
  rate = 1,
  dates,
  propertySize,
  onRemove,
}) => {
  const { T, formatDate } = useIntl();

  const filterValues = [
    ...types,
    ...furniture,
    ...bills,
    ...suitableFor,
    ...rules,
    ...registration,
    ...gender,
    ...searchArea,
    ...facilities,
    ...amenities,
    recentlyAdded,
    radius,
  ];

  const tagList = React.useMemo(() => getTagList(T), [T]);

  return (
    <Grid container spacing={1}>
      {tagList.map(
        ({ category, value, label }, index) =>
          filterValues.includes(value) && (
            <Grid item key={index}>
              <Chip
                variant="outlined"
                onDelete={() => onRemove(category, value)}
                label={T(label)}
                data-test-locator={`FiltersTags ${value} Tag`}
              />
            </Grid>
          ),
      )}

      {rooms.bedroomCount.map((val, index) => (
        <Grid item key={index}>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('bedroomCount', val)}
            label={
              val === SearchFilters.RoomsValue.ONE
                ? T('%s bedroom', val)
                : T('%s bedrooms', val)
            }
            data-test-locator="FiltersTags Rooms Tag"
          />
        </Grid>
      ))}

      {price && (price.min !== null || price.max !== null) && (
        <Grid item>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('price')}
            label={priceRange(
              price.currency,
              price.min !== null ? Math.floor(price.min * rate) : null,
              price.max !== null ? Math.floor(price.max * rate) : null,
            )}
            data-test-locator="FiltersTags Price Tag"
          />
        </Grid>
      )}

      {dates?.startDate && (
        <Grid item>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('dates')}
            label={formatDateRange(
              formatDate,
              dates.startDate,
              dates.endDate || undefined,
            )}
            data-test-locator="FiltersTags Dates Tag"
          />
        </Grid>
      )}

      {propertySize && (!!propertySize.sizeMin || !!propertySize.sizeMax) && (
        <Grid item>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('propertySize')}
            label={
              !propertySize.sizeMax
                ? `${propertySize.sizeMin} m² ${T('option.or_more')}`
                : `${propertySize.sizeMin || 0} m² - ${propertySize.sizeMax} m²`
            }
            data-test-locator="FiltersTags PropertySize Tag"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default FiltersTags;
